import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Card,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { useToast } from '../hooks/useToastContext';
import {
  createManualIntake,
  uploadMultipleFilesAndCreateIntakeIntakeUploadMultiplePost,
  updateIntakeDocument,
} from '../services/api';
import { getBusinessErrorMessage } from '../utils/apiErrorDetail';
import { ORDER_TYPE_OPTIONS } from '../constants/orderTypes';
import { FeatureFlag } from '../constants/featureFlags';
import { useStore } from '../store';

// Simple Progress component
const Progress: React.FC<{ value?: number; className?: string }> = ({
  value,
  className = '',
}) => (
  <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
    <div
      className='bg-blue-600 h-2 rounded-full transition-all duration-300'
      style={{ width: value !== undefined ? `${value}%` : '100%' }}
    />
  </div>
);

interface NewIntakeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIntakeCreated?: (intakeId: string) => void;
}

interface FileUploadState {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  intakeId?: string;
  orderTypeTag?: string;
}

const NewIntakeDialog: React.FC<NewIntakeDialogProps> = ({
  open,
  onOpenChange,
  onIntakeCreated,
}) => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fakeExtraction, setFakeExtraction] = useState(false);
  const [isCreatingManual, setIsCreatingManual] = useState(false);
  const navigate = useNavigate();

  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
  const hasOrderManagerFeatureFlag = hasFeatureFlag(FeatureFlag.ORDER_MANAGER);

  const { success, error } = useToast();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      // One dialog creates one intake: no files while the manual create is in flight.
      if (isCreatingManual) return;

      const droppedFiles = Array.from(e.dataTransfer.files);
      const pdfFiles = droppedFiles.filter(
        file => file.type === 'application/pdf'
      );

      if (pdfFiles.length > 0) {
        const newFiles: FileUploadState[] = pdfFiles.map(file => ({
          file,
          status: 'pending' as const,
          progress: 0,
        }));
        setFiles(prev => [...prev, ...newFiles]);
      } else {
        error('Invalid File', 'Please select PDF files only.');
      }
    },
    [error, isCreatingManual]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (isCreatingManual) {
        e.target.value = '';
        return;
      }
      if (selectedFiles && selectedFiles.length > 0) {
        const pdfFiles = Array.from(selectedFiles).filter(
          file => file.type === 'application/pdf'
        );

        if (pdfFiles.length > 0) {
          const newFiles: FileUploadState[] = pdfFiles.map(file => ({
            file,
            status: 'pending' as const,
            progress: 0,
          }));
          setFiles(prev => [...prev, ...newFiles]);
        } else {
          error('Invalid File', 'Please select PDF files only.');
        }
      }
      // Reset input value to allow selecting the same files again
      e.target.value = '';
    },
    [error, isCreatingManual]
  );

  const handleUpload = async () => {
    if (files.length === 0) return;

    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    // Mark all files as uploading
    setFiles(prev =>
      prev.map(f =>
        f.status === 'pending'
          ? { ...f, status: 'uploading' as const, progress: 0 }
          : f
      )
    );

    try {
      // Simulate progress for all files
      const progressInterval = setInterval(() => {
        setFiles(prev =>
          prev.map(f =>
            f.status === 'uploading' && f.progress < 90
              ? { ...f, progress: f.progress + 10 }
              : f
          )
        );
      }, 200);

      // Upload all files together using the new multi-file endpoint
      const response =
        await uploadMultipleFilesAndCreateIntakeIntakeUploadMultiplePost({
          body: {
            files: pendingFiles.map(f => f.file),
          },
          query: {
            trigger_parsing: !fakeExtraction,
            fake_extraction: fakeExtraction,
          },
        });

      clearInterval(progressInterval);

      // Check if the response was successful
      if (!response || !response.data || !response.data.intake) {
        throw new Error('Upload failed - no intake data received');
      }

      const intake = response.data.intake;

      // Update all files to success
      setFiles(prev =>
        prev.map(f =>
          f.status === 'uploading'
            ? {
                ...f,
                status: 'success' as const,
                progress: 100,
                intakeId: intake.id,
              }
            : f
        )
      );

      // Apply order type tags to documents that were tagged
      const documents = intake.documents || [];
      const taggedFiles = pendingFiles.filter(f => f.orderTypeTag);
      if (taggedFiles.length > 0 && documents.length > 0) {
        for (const taggedFile of taggedFiles) {
          // Match by filename
          const doc = documents.find(
            (d: { file_name: string }) => d.file_name === taggedFile.file.name
          );
          if (doc && taggedFile.orderTypeTag) {
            try {
              await updateIntakeDocument({
                path: { document_id: doc.id },
                body: { task_type_tag: taggedFile.orderTypeTag },
              });
            } catch (err) {
              console.error('Failed to tag document', err);
            }
          }
        }
      }

      // Show success message
      success(
        'Upload Complete',
        `${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''} uploaded successfully as one intake.`
      );

      // Close dialog immediately and clear files
      onOpenChange(false);
      setFiles([]);
      setFakeExtraction(false);

      // Notify parent component after dialog is closed
      if (onIntakeCreated) {
        onIntakeCreated(intake.id);
      }
    } catch (err) {
      console.error('Failed to upload intake files:', err);
      setFiles(prev =>
        prev.map(f =>
          f.status === 'uploading'
            ? {
                ...f,
                status: 'error' as const,
                error: 'Upload failed',
              }
            : f
        )
      );

      error('Upload Failed', 'Failed to upload files. Please try again.');
    }
  };

  // No documents: the backend creates the intake without starting extraction,
  // and the user fills it in by hand on the intake page.
  const handleStartWithoutDocuments = async () => {
    setIsCreatingManual(true);
    try {
      const { data } = await createManualIntake({
        throwOnError: true,
      });
      setFakeExtraction(false);
      onOpenChange(false);
      navigate(`/intake/${data.id}`);
    } catch (err) {
      console.error('Failed to create intake without documents:', err);
      error(
        'Create Failed',
        getBusinessErrorMessage(err) ?? 'Failed to create intake.'
      );
    } finally {
      setIsCreatingManual(false);
    }
  };

  const handleRemoveFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleOrderTypeChange = useCallback((index: number, value: string) => {
    setFiles(prev =>
      prev.map((f, idx) =>
        idx === index
          ? { ...f, orderTypeTag: value === 'none' ? undefined : value }
          : f
      )
    );
  }, []);

  const handleCancel = () => {
    const isUploading = files.some(f => f.status === 'uploading');
    if (isUploading || isCreatingManual) return; // Don't allow canceling mid-request

    setFiles([]);
    setFakeExtraction(false);
    onOpenChange(false);
  };

  // Radix close paths (X, Escape, overlay click) call onOpenChange directly;
  // route them through handleCancel so files/fakeExtraction reset on every
  // close and a reopened dialog can't silently create sample data.
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    handleCancel();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileStatusIcon = (status: FileUploadState['status']) => {
    switch (status) {
      case 'uploading':
        return <Loader2 className='h-5 w-5 animate-spin text-blue-500' />;
      case 'success':
        return <CheckCircle className='h-5 w-5 text-green-500' />;
      case 'error':
        return <AlertCircle className='h-5 w-5 text-red-500' />;
      default:
        return <FileText className='h-5 w-5 text-red-500' />;
    }
  };

  const isUploading = files.some(f => f.status === 'uploading');
  const hasFiles = files.length > 0;
  const hasPendingFiles = files.some(f => f.status === 'pending');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-lg max-h-[90vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle>New Intake - Upload Documents</DialogTitle>
        </DialogHeader>

        <div className='space-y-6 overflow-y-auto px-1 min-h-0'>
          {/* File Upload Area */}
          <Card
            className={`border-2 border-dashed p-8 text-center transition-colors ${
              isDragOver
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
            <h3 className='text-lg font-semibold mb-2'>Upload PDF Documents</h3>
            <p className='text-muted-foreground mb-4'>
              Drag and drop your referral PDFs here, or click to browse
            </p>
            <Button
              variant='outline'
              onClick={() => document.getElementById('file-input')?.click()}
              disabled={isUploading || isCreatingManual}
            >
              Browse Files
            </Button>
            <input
              id='file-input'
              type='file'
              accept='.pdf,application/pdf'
              multiple
              onChange={handleFileSelect}
              className='hidden'
            />
          </Card>

          {!hasFiles && (
            <div>
              <Button
                variant='ghost'
                onClick={handleStartWithoutDocuments}
                disabled={isCreatingManual}
              >
                {isCreatingManual && (
                  <Loader2 className='h-4 w-4 animate-spin' />
                )}
                Start without documents
              </Button>
              <p className='text-sm text-muted-foreground px-4'>
                For test patients or manual entry. AI extraction won&apos;t run.
              </p>
            </div>
          )}

          {/* Selected Files Display */}
          {hasFiles && (
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <h4 className='text-sm font-medium'>
                  Selected Files ({files.length})
                </h4>
              </div>
              <div className='space-y-2 max-h-64 overflow-y-auto w-full max-w-md'>
                {files.map((fileState, index) => (
                  <Card key={index}>
                    <div className='flex items-start space-x-3'>
                      {getFileStatusIcon(fileState.status)}
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium truncate'>
                          {fileState.file.name}
                        </p>
                        <p className='text-xs text-muted-foreground'>
                          {formatFileSize(fileState.file.size)} • PDF Document
                        </p>
                        {fileState.status === 'uploading' && (
                          <Progress
                            value={fileState.progress}
                            className='w-full mt-2'
                          />
                        )}
                        {fileState.status === 'pending' &&
                          hasOrderManagerFeatureFlag && (
                            <Select
                              value={fileState.orderTypeTag || 'none'}
                              onValueChange={val =>
                                handleOrderTypeChange(index, val)
                              }
                            >
                              <SelectTrigger
                                selectSize='sm'
                                className='w-[140px] mt-1'
                              >
                                <SelectValue placeholder='Order type' />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value='none'>
                                  No order type
                                </SelectItem>
                                {ORDER_TYPE_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        {fileState.status === 'error' && fileState.error && (
                          <p className='text-xs text-red-500 mt-1'>
                            {fileState.error}
                          </p>
                        )}
                      </div>
                      {fileState.status === 'pending' && (
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => handleRemoveFile(index)}
                          className='text-red-500 hover:text-red-700'
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Processing Info */}
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
            <h4 className='text-sm font-medium text-blue-900 mb-2'>
              Background Processing
            </h4>
            <p className='text-sm text-blue-700'>
              After upload, the document(s) will be automatically processed for
              text extraction and AI analysis in the background. You can view
              the processing status in the intake history.
            </p>
          </div>

          {/* Dev-only: backend 404s this option outside non-prod stages */}
          {import.meta.env.DEV && (
            <label className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Checkbox
                checkboxSize='sm'
                checked={fakeExtraction}
                onCheckedChange={checked => setFakeExtraction(checked === true)}
                disabled={isUploading}
              />
              Dev: skip extraction and fill intake with sample data
            </label>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={handleCancel}
            disabled={isUploading || isCreatingManual}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!hasPendingFiles || isUploading || isCreatingManual}
          >
            {isUploading ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Uploading...
              </>
            ) : (
              `Upload ${files.length > 0 ? `${files.filter(f => f.status === 'pending').length} ` : ''}File${files.filter(f => f.status === 'pending').length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewIntakeDialog;
